import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth, OptionContentType} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

import BenchChip from './BenchChip';
import Chip, {ChipProps} from './Chip';

interface OptionalId {
  id?: string | null;
}

/**
 * Renders a single chip to summarize a list of items. Your items can be of any type; simply provide
 * the label (e.g., "Entity") and a function to summarize an item (e.g., render its name or id).
 *
 * A typical rendering will show the chip text as "800 Entity links", with a tooltip like "t001, t002..."
 */

export interface SummaryChipProps<T> extends Omit<ChipProps, 'text' | 'tooltipTitle'> {
  items: Array<T>;
  itemSummarizer: (item: T) => string;
  itemLabel: string;
  maxItemsInTooltip?: number;
  isPopover?: boolean;
}

interface SummaryChipContentProps {
  children: JSX.Element;
  menuItems: Array<JSX.Element>;
}

function SummaryChipDropdown(props: SummaryChipContentProps): JSX.Element {
  return (
    <Dropdown
      menu={
        <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FIT_CONTENT} scrollable={true}>
          {props.menuItems}
        </Menu>
      }
    >
      {props.children}
    </Dropdown>
  );
}

function SummaryChipPopover(props: SummaryChipContentProps): JSX.Element {
  return (
    <Popover
      // Open on click so the summary chip toggles via both pointer and keyboard (Enter/Space); the
      // default (unset) interaction is hover-only, which never opens for keyboard users.
      interaction={FloatingInteraction.CLICK}
      // The summarized items are a read-only list of chips, so nothing should pull focus onto the first
      // item when the popover opens (which would draw a focus ring around it). `keepFocusOnToggle` leaves
      // focus on the toggle and makes Tab move into the list, and `disableInitialAutoFocus` stops the Menu
      // from auto-focusing its first option on mount; arrow keys navigate the items once focus is inside.
      keepFocusOnToggle={true}
      renderContent={() => (
        <Menu
          className={css(styles.chipMenu)}
          height={MenuHeight.MEDIUM}
          width={MenuWidth.FIT_CONTENT}
          scrollable={true}
          disableInitialAutoFocus={true}
        >
          {props.menuItems}
        </Menu>
      )}
    >
      {props.children}
    </Popover>
  );
}

export default function SummaryChip<T extends OptionalId>({
  items,
  itemLabel,
  itemSummarizer,
  maxItemsInTooltip = 3,
  isPopover,
  ...passThroughProps
}: SummaryChipProps<T>): JSX.Element {
  const itemClassName = classNames(passThroughProps.className, css(styles.chipSummaryItem));
  const getChip = (item: T): JSX.Element =>
    item.id ? (
      <BenchChip id={item.id} {...passThroughProps} className={itemClassName} isLink={true} onRemove={null} />
    ) : (
      <Chip className={itemClassName} text={itemSummarizer(item)} {...passThroughProps} onRemove={null} />
    );

  const menuItems = items.map((item, idx) => (
    <Option value={idx} key={idx} contentType={OptionContentType.PLAIN}>
      <div className={css(styles.menuItemOption)}>{getChip(item)}</div>
    </Option>
  ));

  const summaryChipButton = (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      className={css(passThroughProps.onRemove && styles.chipSummaryToggleButton)}
      isDropdown={false}
      size={ButtonSize.MINI}
      text={`${menuItems.length} ${pluralize(menuItems.length, itemLabel)}`}
    />
  );

  return (
    <span className={css(styles.chipSummary)}>
      {isPopover ? (
        <SummaryChipPopover menuItems={menuItems}>{summaryChipButton}</SummaryChipPopover>
      ) : (
        <SummaryChipDropdown menuItems={menuItems}>{summaryChipButton}</SummaryChipDropdown>
      )}
      {passThroughProps.onRemove && (
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          className={css(styles.chipSummaryRemoveButton)}
          onClick={(e) => {
            passThroughProps.onRemove!(e);
          }}
          icon={<BenchlingIcon className="chip-button" icon="cross-thick" />}
        />
      )}
    </span>
  );
}

const styles = StyleSheet.create({
  chipSummary: {
    margin: '2px 0 0 2px',
    whiteSpace: 'nowrap',
  },
  chipSummaryToggleButton: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  chipSummaryRemoveButton: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  chipSummaryItem: {
    whiteSpace: 'nowrap',
  },
  menuItemOption: {
    width: 'fit-content',
  },
  // Required for rendering the Menu as a popover (which is necessary when the SummaryChip is inside a
  // popover). This removes the menu boxshadow and ensures the popover renders correctly according to the
  // popover arrow.
  chipMenu: {
    position: 'inherit',
    boxShadow: 'none',
  },
});
